import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Shield, Edit, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';

function groupByCategory(permissions) {
  return permissions.reduce((acc, p) => ({
    ...acc,
    [p.category]: [...(acc[p.category] || []), p]
  }), {});
}

function PermChecklist({ permsByCategory, target, onToggle }) {
  const hasAll = (target.permissions || []).includes('all');
  return (
    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
      {hasAll && (
        <p className="text-xs bg-orange-50 text-orange-700 rounded-md px-3 py-2">
          Este rol tiene <b>acceso total (&apos;all&apos;)</b>. Los permisos individuales se ignoran.
        </p>
      )}
      {Object.entries(permsByCategory).map(([cat, perms]) => (
        <div key={cat}>
          <p className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1.5 font-semibold">{cat}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {perms.map(p => {
              const checked = (target.permissions || []).includes(p.permission_id);
              return (
                <button
                  type="button"
                  key={p.permission_id}
                  onClick={() => onToggle(p.permission_id)}
                  className={`flex items-center gap-2 px-2.5 py-2 rounded-md border text-left text-xs transition-colors ${checked ? 'bg-[#EEF2FF] border-[#4338CA] text-[#3730A3]' : 'bg-white border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]'}`}
                  data-testid={`perm-toggle-${p.permission_id}`}
                >
                  <span className={`shrink-0 w-4 h-4 rounded flex items-center justify-center border ${checked ? 'bg-[#4338CA] border-[#4338CA]' : 'border-[#CBD5E1]'}`}>
                    {checked && <Check size={11} className="text-white" />}
                  </span>
                  <span className="leading-tight">{p.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [newRole, setNewRole] = useState({ role_name: '', permissions: [] });

  const loadData = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([api.getRoles(), api.getPermissions()]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch (error) {
      toast.error('SalesPRO: Error al cargar roles');
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { (async () => { await loadData(); })(); }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const permsByCategory = groupByCategory(permissions);

  const openEdit = (role) => {
    setEditRole({ ...role, permissions: [...(role.permissions || [])] });
    setEditOpen(true);
  };

  const toggleEditPerm = (permId) => {
    setEditRole(prev => {
      const cur = prev.permissions || [];
      return { ...prev, permissions: cur.includes(permId) ? cur.filter(p => p !== permId) : [...cur, permId] };
    });
  };

  const toggleNewPerm = (permId) => {
    setNewRole(prev => {
      const cur = prev.permissions || [];
      return { ...prev, permissions: cur.includes(permId) ? cur.filter(p => p !== permId) : [...cur, permId] };
    });
  };

  const saveEdit = async () => {
    try {
      await api.updateRole(editRole.role_id, { role_name: editRole.role_name, permissions: editRole.permissions });
      toast.success('SalesPRO: Rol actualizado');
      setEditOpen(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Error al actualizar rol');
    }
  };

  const saveCreate = async () => {
    if (!newRole.role_name.trim()) { toast.error('SalesPRO: Ingresa un nombre de rol'); return; }
    try {
      await api.createRole({ role_name: newRole.role_name.trim(), permissions: newRole.permissions });
      toast.success('SalesPRO: Rol creado');
      setCreateOpen(false);
      setNewRole({ role_name: '', permissions: [] });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Error al crear rol');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full" data-testid="roles-loading">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="roles-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22]">Roles y Permisos</h1>
          <p className="text-sm text-[#64748B] mt-1">Crea roles y define qué puede hacer cada uno</p>
        </div>
        <Button size="sm" onClick={() => { setNewRole({ role_name: '', permissions: [] }); setCreateOpen(true); }} data-testid="add-role-button">
          <Plus size={16} className="mr-1.5" /> Nuevo Rol
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => (
          <Card key={role.role_id} className="border border-[#E2E8F0] shadow-sm" data-testid={`role-card-${role.role_id}`}>
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-[#F1F5F9] rounded-md">
                  <Shield size={22} className="text-primary" />
                </div>
                <Button size="sm" variant="outline" onClick={() => openEdit(role)} data-testid={`edit-role-${role.role_id}`}>
                  <Edit size={14} className="mr-1" /> Editar
                </Button>
              </div>
              <CardTitle className="font-heading text-lg font-bold">{role.role_name}</CardTitle>
              <p className="text-xs text-[#64748B] font-mono">{role.role_id}</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm font-medium text-[#64748B]">Permisos ({role.permissions.length}):</p>
                <div className="flex flex-wrap gap-1.5">
                  {role.permissions.includes('all') ? (
                    <span className="px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded-md font-medium">Acceso total</span>
                  ) : role.permissions.slice(0, 8).map((perm) => (
                    <span key={perm} className="px-2 py-1 bg-[#F1F5F9] text-xs rounded-md text-[#022C22]">{perm}</span>
                  ))}
                  {!role.permissions.includes('all') && role.permissions.length > 8 && (
                    <span className="px-2 py-1 text-xs text-[#94A3B8]">+{role.permissions.length - 8} más</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Role Dialog */}
      {editRole && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-role-dialog">
            <DialogHeader>
              <DialogTitle>Editar rol — {editRole.role_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre del rol</Label>
                <Input value={editRole.role_name} onChange={(e) => setEditRole({ ...editRole, role_name: e.target.value })} data-testid="edit-role-name" />
              </div>
              <PermChecklist permsByCategory={permsByCategory} target={editRole} onToggle={toggleEditPerm} />
              <Button onClick={saveEdit} className="w-full" data-testid="save-role-button">Guardar cambios</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Role Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-role-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo rol</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre del rol</Label>
              <Input value={newRole.role_name} onChange={(e) => setNewRole({ ...newRole, role_name: e.target.value })} placeholder="Ej: Supervisor de ventas" data-testid="new-role-name" />
            </div>
            <PermChecklist permsByCategory={permsByCategory} target={newRole} onToggle={toggleNewPerm} />
            <Button onClick={saveCreate} className="w-full" data-testid="create-role-confirm">Crear rol</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
